/**
 * Notification Page (Mockup UI)
 * Displays mock notifications for future feature
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, CheckCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

const MOCK_NOTIFICATIONS = [
  {
    id: '1',
    title: 'บรรลุเป้าหมายวันนี้แล้ว!',
    body: 'คุณอ่านครบเป้าหมายรายวันแล้ว ยินดีด้วย! 🎉',
    read: false,
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '2',
    title: 'อย่าลืมบันทึกการอ่านวันนี้',
    body: 'ยังไม่ได้บันทึกเซสชันการอ่านวันนี้',
    read: false,
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '3',
    title: 'คุณอ่านครบ 100 หน้าแล้ว!',
    body: 'ความคืบหน้าการอ่านกุรอานของคุณดีมาก',
    read: true,
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
];

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 60) return `${diffMins} นาทีที่แล้ว`;
    if (diffHours < 24) return `${diffHours} ชั่วโมงที่แล้ว`;
    if (diffDays === 1) return 'เมื่อวาน';
    if (diffDays < 7) return `${diffDays} วันที่แล้ว`;
    return d.toLocaleDateString('th-TH');
  } catch {
    return '';
  }
}

export function NotificationPage() {
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAllRead = () => {
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read: true }))
    );
  };

  return (
    <div className="min-h-screen p-4 pb-24">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Bell className="h-6 w-6 text-primary" />
          การแจ้งเตือน
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {unreadCount > 0
            ? `คุณมี ${unreadCount} การแจ้งเตือนที่ยังไม่ได้อ่าน`
            : 'ไม่มีการแจ้งเตือนใหม่'}
        </p>
      </header>

      {unreadCount > 0 && (
        <div className="mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={markAllRead}
            className="gap-2"
          >
            <CheckCheck className="h-4 w-4" />
            ทำเครื่องหมายว่าอ่านทั้งหมด
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {notifications.map((item) => (
          <Card
            key={item.id}
            className={cn(
              'bg-card border-border transition-colors',
              !item.read && 'border-l-4 border-l-primary'
            )}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start gap-3">
                {!item.read && (
                  <span
                    className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary"
                    aria-hidden
                  />
                )}
                <div className={cn('min-w-0 flex-1', !item.read && 'pl-2')}>
                  <CardTitle className="text-base font-semibold text-foreground">
                    {item.title}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {item.body}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {formatTime(item.createdAt)}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0" />
          </Card>
        ))}
      </div>

      <p className="text-center text-xs text-muted-foreground mt-8">
        นี่เป็น UI ตัวอย่าง การแจ้งเตือนจริงจะเชื่อมกับระบบในอนาคต
      </p>
    </div>
  );
}

export default NotificationPage;
